import { useRef } from 'react';
import {
  ACCEPTED_CONTENT_TYPES,
  DOCUMENT_KINDS,
  DOCUMENT_LABEL,
  MAX_UPLOAD_BYTES,
  errorMessage,
  mintDocumentSlot,
  uploadToSpaces,
  type DocumentKind,
  type Errors,
  type KybDraft,
  type UploadState,
} from '~/lib/kyb';
import { cn } from '~/lib/cn';
import { SectionCard } from './Primitives';

/**
 * Two upload slots (CAC + Memorandum). Each is a self-contained button that
 * handles its own mint → PUT → done flow. Keeps the surface narrow: the
 * parent gets a stable UploadState per kind, and the review step reads
 * state.documentId when it's time to submit.
 */
export function DocumentsStep({
  draft,
  errors,
  update,
}: {
  draft: KybDraft;
  errors: Errors;
  update: (patch: Partial<KybDraft>) => void;
}): JSX.Element {
  function setSlot(kind: DocumentKind, state: UploadState): void {
    update({ documents: { ...draft.documents, [kind]: state } });
  }

  return (
    <SectionCard
      title="Documents"
      description="PDFs or clear photos. Each file up to 10 MB. We upload directly to encrypted storage — the file never touches Seven's app servers."
    >
      <div className="flex flex-col gap-4">
        {DOCUMENT_KINDS.map((kind) => (
          <UploadSlot
            key={kind}
            kind={kind}
            state={draft.documents[kind]}
            error={errors[`documents.${kind}`]}
            onChange={(s) => setSlot(kind, s)}
          />
        ))}
      </div>
    </SectionCard>
  );
}

function UploadSlot({
  kind,
  state,
  error,
  onChange,
}: {
  kind: DocumentKind;
  state: UploadState;
  error?: string;
  onChange: (s: UploadState) => void;
}): JSX.Element {
  const inputRef = useRef<HTMLInputElement | null>(null);

  async function onPick(file: File): Promise<void> {
    if (file.size > MAX_UPLOAD_BYTES) {
      onChange({
        kind: 'failed',
        fileName: file.name,
        message: `File is ${(file.size / 1024 / 1024).toFixed(1)} MB — the limit is 10 MB.`,
      });
      return;
    }
    if (!(ACCEPTED_CONTENT_TYPES as readonly string[]).includes(file.type)) {
      onChange({
        kind: 'failed',
        fileName: file.name,
        message: 'Only PDF, JPEG, or PNG files are accepted.',
      });
      return;
    }

    onChange({ kind: 'uploading', fileName: file.name, progress: 0 });
    try {
      const slot = await mintDocumentSlot(kind, file.type, file.size);
      await uploadToSpaces(slot.uploadUrl, file);
      onChange({
        kind: 'uploaded',
        fileName: file.name,
        documentId: slot.documentId,
      });
    } catch (err) {
      onChange({
        kind: 'failed',
        fileName: file.name,
        message: errorMessage(err, 'Upload failed. Try again.'),
      });
    }
  }

  const label = DOCUMENT_LABEL[kind];

  return (
    <div
      className={cn(
        'rounded-md border bg-surface-canvas p-4',
        error ? 'border-stop/40' : 'border-rule-hairline',
      )}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[14px] font-semibold text-ink-primary">
            {label}
          </div>
          <div className="mt-0.5 text-[11.5px] text-ink-watermark">
            PDF, JPEG, or PNG · up to 10 MB
          </div>
        </div>

        <input
          ref={inputRef}
          type="file"
          accept=".pdf,.jpg,.jpeg,.png,application/pdf,image/jpeg,image/png"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onPick(file);
            // Reset the input value so re-selecting the same file re-triggers.
            e.target.value = '';
          }}
          className="hidden"
        />

        {state.kind === 'uploaded' ? (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            className="text-[13.5px] font-medium text-ink-secondary hover:text-ink-primary"
          >
            Replace
          </button>
        ) : (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={state.kind === 'uploading'}
            className="inline-flex h-8 shrink-0 items-center rounded-md border border-rule-divider bg-surface-slip px-3 text-[13.5px] font-medium text-ink-primary hover:bg-surface-margin disabled:opacity-60"
          >
            {state.kind === 'uploading' ? 'Uploading…' : 'Choose file'}
          </button>
        )}
      </div>

      <StatusLine state={state} />
      {error && state.kind !== 'uploaded' && (
        <div className="mt-2 text-[13px] text-stop">{error}</div>
      )}
    </div>
  );
}

function StatusLine({ state }: { state: UploadState }): JSX.Element | null {
  if (state.kind === 'empty') return null;
  if (state.kind === 'uploading') {
    return (
      <div className="mt-3 flex items-center gap-2 text-[13px] text-ink-secondary">
        <Spinner />
        <span className="truncate">Uploading {state.fileName}…</span>
      </div>
    );
  }
  if (state.kind === 'uploaded') {
    return (
      <div className="mt-3 flex items-center gap-2 text-[13px] text-tally">
        <svg
          width="14"
          height="14"
          viewBox="0 0 16 16"
          fill="none"
          stroke="currentColor"
          strokeWidth={2}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M3.5 8.5l3 3 6-6" />
        </svg>
        <span className="truncate">{state.fileName}</span>
      </div>
    );
  }
  return (
    <div className="mt-3 flex items-start gap-2 text-[13px] text-stop">
      <svg
        width="14"
        height="14"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth={2}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mt-[1px] flex-none"
      >
        <circle cx="8" cy="8" r="6.5" />
        <path d="M8 5v3M8 11v.5" />
      </svg>
      <span>{state.message}</span>
    </div>
  );
}

function Spinner(): JSX.Element {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" className="animate-spin text-ink-watermark">
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.2" fill="none" />
      <path
        d="M22 12a10 10 0 00-10-10"
        stroke="currentColor"
        strokeWidth="3"
        strokeLinecap="round"
        fill="none"
      />
    </svg>
  );
}
